import { useEffect, useMemo, useState } from 'react'
import { useUiScaleStore } from '@/store/uiScaleStore'

/** TechLayout 发布的 CSS 变量名，供未被外层容器缩放的场景（如地图页悬浮卡片）读取自缩放系数 */
export const PANEL_SELF_SCALE_CSS_VAR = '--app-panel-scale'

/** 可选缩放档位，覆盖从缩小到放大的常用比例 */
export const UI_SCALE_STEPS = [0.5, 0.55, 0.6, 0.65, 0.7, 0.75, 0.8, 0.85, 0.9, 0.95, 1, 1.05, 1.1, 1.15, 1.2] as const
export const MIN_UI_SCALE = UI_SCALE_STEPS[0]
export const MAX_UI_SCALE = UI_SCALE_STEPS[UI_SCALE_STEPS.length - 1]
export const UI_SCALE_STEP_SIZE = 0.05

const SCALE_BASELINE_WIDTH = 1600
const SCALE_BASELINE_HEIGHT = 980

const clampToStepRange = (scale: number) => Math.max(MIN_UI_SCALE, Math.min(scale, MAX_UI_SCALE))

const roundToStep = (scale: number) => Math.round(scale / UI_SCALE_STEP_SIZE) * UI_SCALE_STEP_SIZE

/** 依据当前视口尺寸与设计基准尺寸，计算「推荐」缩放比例 */
const computeRecommendedScale = (viewportWidth: number, viewportHeight: number) => {
  if (!viewportWidth || !viewportHeight) return 1
  const ratio = Math.min(viewportWidth / SCALE_BASELINE_WIDTH, viewportHeight / SCALE_BASELINE_HEIGHT)
  return clampToStepRange(roundToStep(ratio))
}

const getViewportSize = () => {
  if (typeof window === 'undefined') return { width: 0, height: 0 }
  return { width: window.innerWidth, height: window.innerHeight }
}

/**
 * 页面缩放 Hook：
 * - 提供基于当前屏幕分辨率计算出的推荐比例
 * - 提供用户已保存的自定义比例（本地缓存持久化）
 * - 提供逐级调节（放大/缩小/重置为推荐值）的操作方法
 */
export function useUiScale() {
  const hydrated = useUiScaleStore((state) => state.hydrated)
  const hasCustomScale = useUiScaleStore((state) => state.hasCustomScale)
  const customScale = useUiScaleStore((state) => state.customScale)
  const setCustomScale = useUiScaleStore((state) => state.setCustomScale)
  const resetToRecommended = useUiScaleStore((state) => state.resetToRecommended)

  const [viewport, setViewport] = useState(() => getViewportSize())

  useEffect(() => {
    let frameId = 0
    const updateViewport = () => {
      window.cancelAnimationFrame(frameId)
      frameId = window.requestAnimationFrame(() => {
        setViewport((current) => {
          const next = getViewportSize()
          if (current.width === next.width && current.height === next.height) return current
          return next
        })
      })
    }

    window.addEventListener('resize', updateViewport)
    return () => {
      window.cancelAnimationFrame(frameId)
      window.removeEventListener('resize', updateViewport)
    }
  }, [])

  const recommendedScale = useMemo(
    () => computeRecommendedScale(viewport.width, viewport.height),
    [viewport.width, viewport.height]
  )

  // hydrate 完成前使用推荐值兜底，避免闪烁
  const scale = hydrated && hasCustomScale ? clampToStepRange(customScale) : recommendedScale

  const setScale = (next: number) => {
    setCustomScale(clampToStepRange(roundToStep(next)))
  }

  const stepIn = () => setScale(scale + UI_SCALE_STEP_SIZE)
  const stepOut = () => setScale(scale - UI_SCALE_STEP_SIZE)

  return {
    scale,
    recommendedScale,
    isCustom: hasCustomScale,
    isRecommended: Math.abs(scale - recommendedScale) < 1e-6,
    canStepIn: scale < MAX_UI_SCALE,
    canStepOut: scale > MIN_UI_SCALE,
    setScale,
    stepIn,
    stepOut,
    resetToRecommended,
  }
}

const readPanelSelfScaleFromDom = (): number => {
  if (typeof document === 'undefined') return 1
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(PANEL_SELF_SCALE_CSS_VAR)
    .trim()
  const numeric = Number(value)
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 1
}

/**
 * 悬浮卡片（FloatingPanel）自身应叠加的缩放系数。
 * 非地图页：内容已被 TechLayout 外层容器整体缩放，此处应为 1（避免重复缩放）。
 * 地图页：外层容器为保护 ArcGIS 命中检测禁用了整体缩放，悬浮卡片需自行叠加缩放。
 * 具体取值由 TechLayout 通过 --app-panel-scale CSS 变量发布。
 */
export function usePanelSelfScale(): number {
  const [panelSelfScale, setPanelSelfScale] = useState(() => readPanelSelfScaleFromDom())

  useEffect(() => {
    let frameId = 0
    const observer = new MutationObserver(() => {
      window.cancelAnimationFrame(frameId)
      frameId = window.requestAnimationFrame(() => {
        setPanelSelfScale((current) => {
          const next = readPanelSelfScaleFromDom()
          return current === next ? current : next
        })
      })
    })

    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] })
    setPanelSelfScale(readPanelSelfScaleFromDom())

    return () => {
      window.cancelAnimationFrame(frameId)
      observer.disconnect()
    }
  }, [])

  return panelSelfScale
}
